import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { DELETE_LEVEL_OVERRIDE, GET_USER_LEVEL_CONFIG, UPSERT_LEVEL_OVERRIDE } from './graphql';

type LevelEntry = {
  level: number;
  xpToNextLevel: number;
  basicReward?: string | null;
  plusReward?: string | null;
  unlockedFeatures: string[];
  source: 'CODE' | 'OVERRIDE';
};

const LevelConfig = () => {
  const { data, loading, refetch } = useQuery(GET_USER_LEVEL_CONFIG, { fetchPolicy: 'network-only' });
  const [upsertOverride, { loading: saving }] = useMutation(UPSERT_LEVEL_OVERRIDE);
  const [deleteOverride, { loading: deleting }] = useMutation(DELETE_LEVEL_OVERRIDE);

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusVariant, setStatusVariant] = useState<'success' | 'danger'>('success');
  const [editorError, setEditorError] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);

  const [editLevel, setEditLevel] = useState(0);
  const [originalEntry, setOriginalEntry] = useState<LevelEntry | null>(null);
  const [xpToNextLevel, setXpToNextLevel] = useState('');
  const [basicReward, setBasicReward] = useState('');
  const [plusReward, setPlusReward] = useState('');
  const [features, setFeatures] = useState<string[]>([]);

  const levels: LevelEntry[] = useMemo(() => data?.liveOps?.userLevelConfig ?? [], [data?.liveOps?.userLevelConfig]);
  const availableFeatures: string[] = useMemo(
    () => data?.liveOps?.userLevelConfigFeatures ?? [],
    [data?.liveOps?.userLevelConfigFeatures]
  );
  const overriddenCount = levels.filter((entry) => entry.source === 'OVERRIDE').length;

  const onEdit = (entry: LevelEntry) => {
    setEditorError(null);
    setOriginalEntry(entry);
    setEditLevel(entry.level);
    setXpToNextLevel(String(entry.xpToNextLevel));
    setBasicReward(entry.basicReward ?? '');
    setPlusReward(entry.plusReward ?? '');
    setFeatures([...entry.unlockedFeatures]);
    setShowEditor(true);
  };

  const onSave = async () => {
    setEditorError(null);
    if (originalEntry) {
      const unchanged =
        Number(xpToNextLevel) === originalEntry.xpToNextLevel &&
        (basicReward.trim() || null) === (originalEntry.basicReward ?? null) &&
        (plusReward.trim() || null) === (originalEntry.plusReward ?? null) &&
        JSON.stringify(features) === JSON.stringify(originalEntry.unlockedFeatures);
      if (unchanged) {
        setEditorError(
          originalEntry.source === 'CODE'
            ? 'No changes to save — the values already match the code defaults.'
            : 'No changes to save.'
        );
        return;
      }
    }
    try {
      await upsertOverride({
        variables: {
          input: {
            level: editLevel,
            xpToNextLevel: xpToNextLevel.trim() ? Number(xpToNextLevel) : null,
            basicReward: basicReward.trim() || null,
            // Empty field = clear the plus reward (many levels legitimately have none)
            plusReward: plusReward.trim() ? plusReward.trim() : '',
            unlockedFeatures: features,
          },
        },
      });
      setStatusVariant('success');
      setStatusMessage(`Level ${editLevel} override saved. Live within ~1 minute.`);
      setShowEditor(false);
      await refetch();
    } catch (error) {
      setEditorError(error instanceof Error ? error.message : 'Failed to save override.');
    }
  };

  const onRevert = async (entry: LevelEntry) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Revert level ${entry.level} to the shipped code values?`)) {
      return;
    }
    try {
      await deleteOverride({ variables: { level: entry.level } });
      setStatusVariant('success');
      setStatusMessage(`Level ${entry.level} reverted to code values.`);
      await refetch();
    } catch (error) {
      setStatusVariant('danger');
      setStatusMessage(error instanceof Error ? error.message : 'Failed to revert level.');
    }
  };

  return (
    <>
      {statusMessage && (
        <Alert variant={statusVariant} onClose={() => setStatusMessage(null)} dismissible>
          {statusMessage}
        </Alert>
      )}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            User Level Config — the live progression curve (100 levels).
            {overriddenCount > 0 && (
              <Badge variant="info" className="ml-2">
                {overriddenCount} level(s) overridden
              </Badge>
            )}
          </span>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger" className="small">
            This table drives XP progression, level rewards and feature unlocks for EVERY player. Changes go live
            within ~1 minute of saving — double-check before saving, and prefer testing on the test environment
            first. Reward format: <code>identifier:amount</code> (e.g. <code>coin:150</code>,{' '}
            <code>boost_remove2:10</code>, <code>sticker:0:sticker_basic_1</code>).
          </Alert>
          {loading ? (
            <Spinner animation="border" />
          ) : (
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Level</th>
                    <th>XP to next</th>
                    <th>Basic reward</th>
                    <th>Plus reward</th>
                    <th>Unlocks</th>
                    <th>Source</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {levels.map((entry) => (
                    <tr key={entry.level}>
                      <td>{entry.level}</td>
                      <td>{entry.xpToNextLevel === -1 ? 'MAX' : entry.xpToNextLevel}</td>
                      <td>{entry.basicReward ?? '—'}</td>
                      <td>{entry.plusReward ?? '—'}</td>
                      <td style={{ maxWidth: 320 }}>{entry.unlockedFeatures.join(', ') || '—'}</td>
                      <td>
                        <Badge variant={entry.source === 'OVERRIDE' ? 'info' : 'secondary'}>
                          {entry.source === 'OVERRIDE' ? 'Overridden' : 'Code'}
                        </Badge>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Button size="sm" variant="outline-primary" className="mr-2" onClick={() => onEdit(entry)}>
                          Edit
                        </Button>
                        {entry.source === 'OVERRIDE' && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={deleting}
                            onClick={() => onRevert(entry)}
                          >
                            Revert
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showEditor} onHide={() => setShowEditor(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit level {editLevel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>XP to next level</Form.Label>
                <Form.Control
                  type="number"
                  value={xpToNextLevel}
                  onChange={(event) => setXpToNextLevel(event.target.value)}
                />
                <Form.Text className="text-muted">-1 = final level (only valid on the last row)</Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Basic reward</Form.Label>
                <Form.Control
                  placeholder="coin:150"
                  value={basicReward}
                  onChange={(event) => setBasicReward(event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Plus reward (empty = none)</Form.Label>
                <Form.Control
                  placeholder="coin:300"
                  value={plusReward}
                  onChange={(event) => setPlusReward(event.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Unlocked features at this level</Form.Label>
            <div className="mb-2">
              {features.length === 0 && <span className="text-muted">None</span>}
              {features.map((feature) => (
                <Badge
                  key={feature}
                  variant="info"
                  className="mr-1 mb-1"
                  style={{ cursor: 'pointer' }}
                  title="Remove"
                  onClick={() => setFeatures((current) => current.filter((entry) => entry !== feature))}
                >
                  {feature} ✕
                </Badge>
              ))}
            </div>
            <Form.Control
              as="select"
              value=""
              onChange={(event) => {
                const feature = event.currentTarget.value;
                if (feature) {
                  setFeatures((current) => (current.includes(feature) ? current : [...current, feature]));
                }
              }}
            >
              <option value="">Add a feature…</option>
              {availableFeatures
                .filter((feature) => !features.includes(feature))
                .map((feature) => (
                  <option key={feature} value={feature}>
                    {feature}
                  </option>
                ))}
            </Form.Control>
            <Form.Text className="text-muted">
              Click a badge to remove it. Moving a feature unlock also moves its starter pack when the pack trigger is
              &quot;Feature unlock&quot;.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          {editorError && (
            <div className="text-danger mr-auto" style={{ maxWidth: '65%' }}>
              {editorError}
            </div>
          )}
          <Button variant="secondary" onClick={() => setShowEditor(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={saving} onClick={onSave}>
            {saving ? 'Saving…' : 'Save override'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LevelConfig;
